import { eligibleContactKind } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
import type { PatientInfoPolicyFields } from "@ascleo/frontdesk-contracts/patient-info/policy";
import type { ReviewContact } from "@ascleo/frontdesk-contracts/patient-info/review";
import { PATIENT_INFO_CATEGORY_ORDER } from "../../../../../../lib/patient-info/policy-projection";

/**
 * A contact-bearing category (phone or email) as the check sheet shows it.
 *
 * `primary` is the one contact printed on the resting row; `additional` is what the expander
 * reveals. `primary` is `undefined` when the category is enabled but the patient has no contact of
 * that kind on file.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import type { ContactCategoryView } from "./category-model";
 *
 * const view: ContactCategoryView = {
 *   kind: "contacts",
 *   category: "phone",
 *   additional: [],
 * };
 * ```
 */
export interface ContactCategoryView {
  readonly kind: "contacts";
  readonly category: "phone" | "email";
  readonly primary?: ReviewContact;
  readonly additional: readonly ReviewContact[];
}

/**
 * The Hausarzt category, which carries free text rather than contacts.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import type { HausarztCategoryView } from "./category-model";
 *
 * const view: HausarztCategoryView = { kind: "hausarzt", value: "Dr. med. Anna Hoffmann" };
 * ```
 */
export interface HausarztCategoryView {
  readonly kind: "hausarzt";
  readonly value: string | null;
}

/**
 * One row of the check sheet.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import type { CategoryView } from "./category-model";
 *
 * const view: CategoryView = { kind: "hausarzt", value: null };
 * ```
 */
export type CategoryView = ContactCategoryView | HausarztCategoryView;

/**
 * Build the check sheet rows from the review DTO and the practice policy.
 *
 * Only policy-enabled categories produce a row, and rows always come back in the canonical order
 * (phone → email → Hausarzt) so the terminal, the settings companion, and the SOL card describe the
 * same review. A disabled category yields nothing at all — not an empty row, not a placeholder.
 *
 * The primary contact is the one flagged `isPrimary`; if the vendor flags none, the first contact of
 * that kind takes the resting row so the patient is never shown an empty category that in fact has
 * data.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { buildCategoryViews } from "./category-model";
 *
 * buildCategoryViews([], null, { phone: true, email: false, hausarzt: false });
 * // => [{ kind: "contacts", category: "phone", primary: undefined, additional: [] }]
 * ```
 */
export function buildCategoryViews(
  contacts: readonly ReviewContact[],
  hausarzt: string | null,
  fields: PatientInfoPolicyFields,
): readonly CategoryView[] {
  const views: CategoryView[] = [];
  for (const category of PATIENT_INFO_CATEGORY_ORDER) {
    if (!fields[category]) continue;
    if (category === "hausarzt") {
      views.push({ kind: "hausarzt", value: hausarzt });
      continue;
    }
    const owned = contacts.filter(
      (contact) => eligibleContactKind(contact.type) === category,
    );
    const primary = owned.find((contact) => contact.isPrimary) ?? owned[0];
    views.push({
      kind: "contacts",
      category,
      ...(primary === undefined ? {} : { primary }),
      additional: owned.filter((contact) => contact !== primary),
    });
  }
  return views;
}
